import React, { useEffect, useState } from 'react';
import api from '../api';
import StatusBadge, { STATUSES, MACHINES, MATERIAL_STATUSES } from './statusBadge';

const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.floor((new Date(a) - new Date(b)) / 86400000);

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtDueDate = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  const [, m, day] = s.split('-');
  const mi = parseInt(m, 10) - 1;
  if (!MONTH_NAMES[mi] || !day) return d;
  return `${day.padStart(2, '0')}-${MONTH_NAMES[mi]}`;
};

// Toggle to re-enable the overdue / due-soon alert blocks on the dashboard.
const SHOW_DUE_ALERTS = false;

export default function Dashboard({ role }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [machine, setMachine] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState(false);

  const load = () => api.get('/api/orders?archive=Active').then(r => setOrders(r.data || [])).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const t = today();
  const overdue = orders.filter(o => o.due_date && o.due_date < t && o.status !== 'Delivered');
  const dueSoon = orders.filter(o => o.due_date && o.due_date >= t && daysBetween(o.due_date, t) <= 3 && o.status !== 'Delivered');
  const totalValue = orders.reduce((s, o) => s + (Number(o.qty_ordered || 0) * Number(o.sales_price || 0)), 0);

  const filtered = orders.filter(o => {
    if (search && !`${o.part_number} ${o.customer_name} ${o.po_number}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (machine && o.machine_name !== machine) return false;
    if (status && o.status !== status) return false;
    return true;
  });

  const showPricing = role !== 'shop_floor';

  return (
    <div>
      <div className="row">
        <div className="stat red"><div className="label">Past Due</div><div className="value">{overdue.length}</div></div>
        <div className="stat orange"><div className="label">Due ≤ 3 Days</div><div className="value">{dueSoon.length}</div></div>
        <div className="stat navy"><div className="label">Active Orders</div><div className="value">{orders.length}</div></div>
        {showPricing && <div className="stat green"><div className="label">Open Order Value</div><div className="value">${totalValue.toFixed(2)}</div></div>}
      </div>

      {SHOW_DUE_ALERTS && overdue.length > 0 && (
        <div className="alert red"><strong>⚠ {overdue.length} Overdue Orders</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>{overdue.slice(0, 5).map(o => <li key={o.id}>{o.part_number} — {o.customer_name} — due {fmtDueDate(o.due_date)}</li>)}</ul>
        </div>
      )}
      {SHOW_DUE_ALERTS && dueSoon.length > 0 && (
        <div className="alert yellow"><strong>⏰ {dueSoon.length} Due Within 3 Days</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>{dueSoon.slice(0, 5).map(o => <li key={o.id}>{o.part_number} — {o.customer_name} — due {fmtDueDate(o.due_date)}</li>)}</ul>
        </div>
      )}

      <div className="filterbar">
        <input placeholder="Search part, customer, PO…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={machine} onChange={e => setMachine(e.target.value)}><option value="">All Machines</option>{MACHINES.map(m => <option key={m}>{m}</option>)}</select>
        <select value={status} onChange={e => setStatus(e.target.value)}><option value="">All Statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Part Number</th><th>Customer</th><th>PO #</th><th>Machine</th><th>Due Date</th>
            <th>Qty Ordered</th><th>Qty Shipped</th><th>Material Status</th><th>Status</th>{showPricing && <><th>Sales Price</th><th>Total Value</th></>}
          </tr></thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} className="clickable" onClick={() => { setSelected(o); setEdit(false); }}>
                <td>{o.part_number}</td><td>{o.customer_name}</td><td>{o.po_number}</td><td>{o.machine_name}</td><td>{fmtDueDate(o.due_date)}</td>
                <td>{o.qty_ordered}</td><td>{o.qty_shipped}</td><td>{o.raw_material_status}</td>
                <td><StatusBadge status={o.status} /></td>
                {showPricing && <><td>${Number(o.sales_price || 0).toFixed(4)}</td><td>${(Number(o.qty_ordered || 0) * Number(o.sales_price || 0)).toFixed(2)}</td></>}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={showPricing ? 11 : 9} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No orders.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <OrderModal order={selected} edit={edit} setEdit={setEdit} onClose={() => setSelected(null)} onSaved={() => { load(); setSelected(null); }} role={role} />
      )}
    </div>
  );
}

function OrderModal({ order, edit, setEdit, onClose, onSaved, role }) {
  const [form, setForm] = useState(order);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    await api.put(`/api/orders/${order.id}`, form);
    onSaved();
  };
  const archive = async () => {
    await api.put(`/api/orders/${order.id}`, { ...order, archive: 'Archived' });
    onSaved();
  };
  const showPricing = role !== 'shop_floor';
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Order: {order.part_number}</h3>
        {!edit ? (
          <div>
            <p><b>Customer:</b> {order.customer_name}</p>
            <p><b>PO #:</b> {order.po_number}</p>
            <p><b>Machine:</b> {order.machine_name}</p>
            <p><b>Due:</b> {fmtDueDate(order.due_date)}</p>
            <p><b>Qty Ordered:</b> {order.qty_ordered} / <b>Shipped:</b> {order.qty_shipped}</p>
            <p><b>Material Status:</b> {order.raw_material_status}</p>
            <p><b>Status:</b> <StatusBadge status={order.status} /></p>
            {showPricing && <p><b>Sales Price:</b> ${order.sales_price}</p>}
            <p><b>Notes:</b> {order.notes}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn-primary" onClick={() => setEdit(true)}>Edit</button>
              <button className="btn-danger" onClick={archive}>Archive</button>
              <button onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid2">
              <div className="field"><label>Qty Shipped</label><input type="number" value={form.qty_shipped || ''} onChange={e => upd('qty_shipped', e.target.value)} /></div>
              <div className="field"><label>Status</label><select value={form.status || ''} onChange={e => upd('status', e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
              <div className="field"><label>Material Status</label><select value={form.raw_material_status || ''} onChange={e => upd('raw_material_status', e.target.value)}>{MATERIAL_STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
              <div className="field"><label>Due Date</label><input type="date" value={(form.due_date || '').slice(0, 10)} onChange={e => upd('due_date', e.target.value)} /></div>
            </div>
            <div className="field"><label>Notes</label><textarea value={form.notes || ''} onChange={e => upd('notes', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-success" onClick={save}>Save</button>
              <button onClick={() => setEdit(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
